import { Component, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA, resource } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { lastValueFrom, tap } from 'rxjs';
import { BusinessService } from '../../../shared/services/business.service';
import { Business } from '../../../shared/models';
import { AppDataTable, DataColumn, PaginationState } from '../../../shared/components/app-data-table/app-data-table';
import { AppBadge } from '../../../shared/components/app-badge/app-badge';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppMetrics, MetricItem } from '../../../shared/components/app-metrics/app-metrics';
import { AppModal } from '../../../shared/components/app-modal/app-modal';
import { MembershipAssignDialog } from '../../../shared/components/membership-assign-dialog/membership-assign-dialog';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-business-list',
  standalone: true,
  imports: [CommonModule, AppDataTable, AppBadge, AppButton, RouterLink, AppMetrics, AppModal, MembershipAssignDialog],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './business-list.html'
})
export class BusinessList {
  private businessService = inject(BusinessService);
  public authService = inject(AuthService);

  getLogoUrl(path?: string): string | null {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;

    // Si es un path relativo de uploads, concatenar la base del API del backend
    const baseUrl = environment.apiUrl.replace('/api/v1', '').replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  }

  // ── Parámetros de consulta ──────────────────────────────────────────────
  page = signal(1);
  limit = signal(10);
  searchQ = signal('');

  // ── Resource (Angular 19 Pattern) ───────────────────────────────────────
  businessResource = resource({
    params: () => ({
      page: this.page(),
      limit: this.limit(),
      search: this.searchQ()
    }),
    loader: async ({ params }) => {
      const { page, limit, search } = params;
      return lastValueFrom(
        this.businessService.getAll({ page, limit, search }).pipe(
          tap((response: any) => {
            this.businesses.set(response.data || []);
            this.total.set(response.total || response.meta?.total || 0);
            this.stats.set(response.stats || { enabled: 0, suspended: 0, invoicing: 0, total: 0 });
          })
        )
      );
    }
  });

  // ── Estado ──────────────────────────────────────────────────────────────
  businesses = signal<Business[]>([]);
  total = signal(0);
  stats = signal({ enabled: 0, suspended: 0, invoicing: 0, total: 0 });

  showDetails = signal(false);
  selectedBusiness = signal<Business | null>(null);

  viewDetails(biz: Business) {
    this.selectedBusiness.set(biz);
    this.showDetails.set(true);
  }

  // ── Gestión de Membresía ──────────────────────────────────────────────
  showMembershipDialog = signal(false);
  
  openMembership(biz: Business) {
    if (!this.authService.hasPermission('memberships:assign') && !this.authService.isSuperAdmin) return;
    this.selectedBusiness.set(biz);
    this.showMembershipDialog.set(true);
  }


  loading = this.businessResource.isLoading;

  pagination = computed<PaginationState>(() => ({
    current: this.page(),
    limit: this.limit(),
    total: this.total()
  }));

  metricsItems = computed<MetricItem[]>(() => [
    { label: 'Activos', value: this.stats().enabled, icon: 'solar:check-circle-bold-duotone', variant: 'success' },
    { label: 'Suspendidos', value: this.stats().suspended, icon: 'solar:close-circle-bold-duotone', variant: 'warning' },
    { label: 'Facturando', value: this.stats().invoicing, icon: 'solar:bill-list-bold-duotone', variant: 'primary' }
  ]);

  columns: DataColumn[] = [
    { key: 'name', title: 'Empresa', dataIndex: 'name' },
    { key: 'taxId', title: 'RUC / ID', dataIndex: 'taxId' },
    { key: 'codes', title: 'Est / P.E', dataIndex: 'establishmentCode' },
    { key: 'isEnabled', title: 'Estado', dataIndex: 'isEnabled' },
    { key: 'createdAt', title: 'Registro', dataIndex: 'createdAt' },
    { key: 'actions', title: '', align: 'right', class: 'w-24' },
  ];

  handleSearch(query: string) {
    this.searchQ.set(query);
    this.page.set(1);
  }

  handlePageChange(page: number) {
    this.page.set(page);
  }

  handleLimitChange(limit: number) {
    this.limit.set(limit);
    this.page.set(1);
  }

  deleteBusiness(id: string) {
    if (confirm('¿Estás seguro de eliminar este negocio?')) {
      this.businessService.delete(id).subscribe({
        next: () => this.businessResource.reload(),
        error: (err) => {
          console.error('Error al eliminar negocio', err);
          this.businessResource.reload();
        }
      });
    }
  }
}
